import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Boxes,
  CheckCircle2,
  CircleSlash,
  ClipboardCheck,
  Code2,
  Download,
  FileCheck2,
  FileSearch,
  GitPullRequest,
  KeyRound,
  Network,
  Radar,
  Scale,
  Sparkles,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import type { ThemePreference } from "../../api/types.js";
import { brand, consolePath } from "../../app/brand.js";
import { handleInternalNavigation } from "../../app/routing.js";
import { EvaluationPanel } from "./EvaluationPanel.js";
import { ProductVideos, productVideosManifestUrl } from "./ProductVideos.js";

const quickAnswers = [
  {
    label: "What are we selling?",
    value: "A governed security evidence console.",
  },
  {
    label: "Who is it for?",
    value: "Security, AppSec, platform, risk, and engineering teams.",
  },
  {
    label: "Where is it used?",
    value: "Release readiness, audits, customer assurance, remediation, and review queues.",
  },
];

const heroProofPoints = [
  { icon: Radar, label: "Signal", value: "Scanner finding" },
  { icon: Code2, label: "Proof", value: "Source replay" },
  { icon: Scale, label: "Decision", value: "Human-owned" },
];

const deliverables = [
  {
    icon: FileSearch,
    title: "Evidence dossier",
    copy: "Finding, source path, line range, replay state, assumptions, counter-evidence, and hashes in one record.",
  },
  {
    icon: ClipboardCheck,
    title: "Decision queue",
    copy: "Confirmed, refuted, suppressed, and needs-human work separated from raw scanner noise.",
  },
  {
    icon: Boxes,
    title: "SBOM + CBOM desk",
    copy: "Dependency and cryptography inventory tied back to the same reviewed evidence.",
  },
  {
    icon: Wrench,
    title: "Remediation packet",
    copy: "Owner, rationale, proposed change, validation requirement, and audit memory for the fix.",
  },
  {
    icon: GitPullRequest,
    title: "Delivery gate",
    copy: "A source-control-ready answer for what changed, what is still open, and what cannot be claimed.",
  },
  {
    icon: FileCheck2,
    title: "Customer report",
    copy: "A readable proof bundle for security leaders, auditors, and customer assurance reviews.",
  },
];

const fitRows = [
  {
    tool: "IDS, SIEM, EDR",
    does: "Detect runtime behavior and alert on activity.",
    provenance:
      "Packages evidence and decisions around source, findings, fixes, and review history.",
  },
  {
    tool: "Vulnerability managers",
    does: "Track known CVEs, assets, scanner results, and aging.",
    provenance:
      "Explains which claims are supported, limited, disputed, fixed, or ready for customer proof.",
  },
  {
    tool: "SAST, SCA, IaC scanners",
    does: "Generate candidate findings from code, dependencies, and configuration.",
    provenance:
      "Replays citations, preserves limitations, routes decisions, and exports governed proof.",
  },
  {
    tool: "GRC and ticketing",
    does: "Manage policy, tasks, ownership, and audit process.",
    provenance:
      "Prepares evidence-backed decisions and reports for manual handoff through the customer's approved GRC or ticketing process.",
  },
];

const publicDocsHref = "/app/docs?returnTo=%2F";

const workflow = [
  {
    title: "Connect evidence",
    copy: "Authorize a repo, scanner export, SBOM, CBOM, or benchmark scope.",
  },
  {
    title: "Replay the claim",
    copy: "Bind each finding to source, hashes, limitations, and replay status.",
  },
  {
    title: "Decide with context",
    copy: "Review attack path, inventory impact, counter-evidence, and rationale.",
  },
  {
    title: "Deliver proof",
    copy: "Export the report, remediation packet, or delivery preview for an approved engineering handoff.",
  },
];

const goodFit = [
  "Your teams argue about whether scanner findings are real.",
  "You need customer-ready proof without building a custom evidence spreadsheet.",
  "You want source, SBOM, CBOM, decisions, and remediation to share one audit trail.",
  "You need reviewers to see what was proved, what was not, and what changed.",
];

const notFor = [
  "You only need a runtime detection sensor or packet-level IDS.",
  "You want an autonomous blocker that accepts every scanner result without review.",
  "You need generic GRC policy management with no source, finding, or evidence workflow.",
  "You cannot provide authorized source, SBOM, CBOM, benchmark, or scan inputs to review.",
];

export function LandingPage({
  navigate,
  theme = "vantablack",
  onThemeChange,
}: {
  navigate: (path: string) => void;
  theme?: ThemePreference;
  onThemeChange?: (theme: ThemePreference) => void;
}) {
  const openConsole = consolePath("/overview");
  const videosUrl = productVideosManifestUrl();

  return (
    <div className="story-site buyer-site">
      <header className="story-nav buyer-nav">
        <a
          className="story-brand"
          href="/"
          aria-label={`Breakwater ${brand.name} home`}
          onClick={(event) => handleInternalNavigation(event, navigate)}
        >
          <img
            className="story-brand__lockup story-brand__lockup--dark"
            src="/brand/breakwater-provenance-horizontal-dark-no-tagline.svg"
            alt={`Breakwater ${brand.name}`}
          />
          <img
            className="story-brand__lockup story-brand__lockup--light"
            src="/brand/breakwater-provenance-horizontal-light-no-tagline.svg"
            alt=""
            aria-hidden="true"
          />
        </a>
        <nav className="story-nav__links" aria-label="Product sections">
          <a href="#evaluate">Evaluate</a>
          <a href="#fit">Where it fits</a>
          <a href="#buyer-trust">Deployment questions</a>
          {videosUrl && <a href="#videos">Product videos</a>}
          <a href={publicDocsHref} target="_blank" rel="noreferrer">
            Book
          </a>
        </nav>
        <div className="story-nav__actions">
          <fieldset className="buyer-theme-picker">
            <legend className="visually-hidden">Color theme</legend>
            {(["light", "vantablack"] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={theme === value}
                onClick={() => onThemeChange?.(value)}
              >
                {value === "light" ? "Light" : "Vantablack"}
              </button>
            ))}
          </fieldset>
          <details
            className="buyer-mobile-menu"
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.currentTarget.removeAttribute("open");
                event.currentTarget.querySelector("summary")?.focus();
              }
            }}
          >
            <summary>Menu</summary>
            <nav aria-label="Mobile product navigation">
              <a href="#evaluate">Evaluate</a>
              <a href="#fit">Where it fits</a>
              <a href="#buyer-trust">Deployment questions</a>
              {videosUrl && <a href="#videos">Product videos</a>}
              <a href={publicDocsHref}>Documentation · sign-in required</a>
            </nav>
          </details>
          <a
            className="story-signin"
            href={openConsole}
            onClick={(event) => handleInternalNavigation(event, navigate)}
          >
            Sign in
            <ArrowRight aria-hidden="true" />
          </a>
        </div>
      </header>

      <main>
        <section className="buyer-hero" id="what" aria-labelledby="buyer-hero-heading">
          <div className="buyer-hero__copy">
            <p className="story-kicker">
              <BadgeCheck aria-hidden="true" />
              Application security · evidence and review
            </p>
            <h1 id="buyer-hero-heading">
              A finding is a claim.
              <br />
              Make it reviewable.
            </h1>
            <p className="story-hero__lead">
              <strong>Provenance is an application security evidence and review platform.</strong>{" "}
              It binds scanner findings to an exact source snapshot, independent checks, coverage,
              human decisions, and an owned engineering handoff.
            </p>
            <section
              className="buyer-proof-rail"
              aria-label="How Provenance turns a finding into proof"
            >
              {heroProofPoints.map((point) => {
                const Icon = point.icon;
                return (
                  <div key={point.label}>
                    <Icon aria-hidden="true" />
                    <span>{point.label}</span>
                    <strong>{point.value}</strong>
                  </div>
                );
              })}
            </section>
            <div className="story-hero__actions">
              <a className="story-button story-button--primary" href="#evaluate">
                Plan an evaluation
                <ArrowRight aria-hidden="true" />
              </a>
              <a className="story-button story-button--quiet" href="#proof">
                Inspect the review workflow
              </a>
              <a
                className="story-button story-button--quiet story-button--book"
                href={publicDocsHref}
                target="_blank"
                rel="noreferrer"
              >
                Product book
                <Download aria-hidden="true" />
              </a>
            </div>
            <p className="buyer-hero__signin-note">
              The hosted console is for POC and UI evaluation with synthetic or approved benchmark
              data. Assessments of private networks run in your local installation, including an
              air-gapped deployment; this hosted site does not scan customer networks.
            </p>
          </div>

          <aside className="evidence-packet" aria-label="Example Provenance evidence packet">
            <div className="evidence-packet__topline">
              <span>Illustrative evidence demo</span>
              <strong>Synthetic showcase</strong>
            </div>
            <div className="evidence-packet__finding">
              <span className="evidence-packet__severity">High</span>
              <div>
                <strong>Authorization bypass candidate</strong>
                <p>payments/authz.ts · lines 88-119 · replay matched</p>
              </div>
            </div>
            <ol className="evidence-packet__chain" aria-label="Evidence chain">
              <li>
                <Radar aria-hidden="true" />
                Scanner lead
              </li>
              <li>
                <Code2 aria-hidden="true" />
                Source replay
              </li>
              <li>
                <Scale aria-hidden="true" />
                Human decision
              </li>
            </ol>
            <div className="evidence-code-card">
              <div>
                <span>Source citation</span>
                <strong>hash: 9f2c...a81</strong>
              </div>
              <pre>{`if (!policy.allowed(user, action)) {
  audit.warn("denied");
  return allowFallback ? true : false;
}`}</pre>
            </div>
            <dl className="evidence-packet__verdict">
              <div>
                <dt>What is proved</dt>
                <dd>
                  A source reference can be reproduced. That alone does not validate the finding.
                </dd>
              </div>
              <div>
                <dt>What is not claimed</dt>
                <dd>No runtime exploitability without separate approval.</dd>
              </div>
              <div>
                <dt>Next action</dt>
                <dd>Assign owner, fix fail-closed behavior, rerun evidence.</dd>
              </div>
            </dl>
            <p className="evidence-packet__note">
              Synthetic showcase means a seeded demonstration used to explain the workflow. It is
              not live customer evidence and should not be read as a production scan result.
            </p>
          </aside>

          <dl className="buyer-answer-strip" aria-label="Basic product answers">
            {quickAnswers.map((answer) => (
              <div key={answer.label}>
                <dt>{answer.label}</dt>
                <dd>{answer.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <EvaluationPanel product="Provenance" />
        <section className="buyer-section proof-section" id="proof">
          <div className="story-section-heading">
            <p className="story-kicker">
              <Sparkles aria-hidden="true" />
              What you get
            </p>
            <h2>A security answer that comes with its evidence, limits, owner, and next step.</h2>
            <p>
              Provenance is strongest when a team already has detection and scanning tools, but
              lacks a durable way to decide which results are defensible.
            </p>
          </div>
          <div className="deliverable-grid">
            {deliverables.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title}>
                  <Icon aria-hidden="true" />
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="buyer-section fit-section" id="fit">
          <div className="story-section-heading">
            <p className="story-kicker">
              <Network aria-hidden="true" />
              Where it fits
            </p>
            <h2>
              It complements your security stack. It does not replace your sensors or scanners.
            </h2>
            <p>
              Think of Provenance as the evidence and decision layer between tools that produce
              signals and people who must sign their name to an answer.
            </p>
          </div>
          <div className="fit-table-scroll">
            <table className="fit-table" aria-label="How Provenance fits beside existing tools">
              <thead>
                <tr className="fit-table__header">
                  <th scope="col">Existing tool</th>
                  <th scope="col">What it does</th>
                  <th scope="col">Where Provenance fits</th>
                </tr>
              </thead>
              <tbody>
                {fitRows.map((row) => (
                  <tr className="fit-table__row" key={row.tool}>
                    <th scope="row">{row.tool}</th>
                    <td>{row.does}</td>
                    <td>{row.provenance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section
          className="buyer-section workflow-section"
          id="use"
          aria-labelledby="workflow-heading"
        >
          <div className="story-section-heading story-section-heading--center">
            <p className="story-kicker">
              <KeyRound aria-hidden="true" />
              How to use it
            </p>
            <h2 id="workflow-heading">From scanner output to a defendable security decision.</h2>
          </div>
          <ol className="workflow-timeline">
            {workflow.map((step, index) => (
              <li key={step.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="buyer-section fit-test-section" id="not-for">
          <div className="story-section-heading story-section-heading--center">
            <p className="story-kicker">
              <TriangleAlert aria-hidden="true" />
              Fit test
            </p>
            <h2>
              Use Provenance when proof matters. Skip it when you need a different class of tool.
            </h2>
          </div>
          <div className="fit-test-grid">
            <article className="fit-test-card fit-test-card--yes">
              <h3>
                <CheckCircle2 aria-hidden="true" />
                Consider Provenance when
              </h3>
              {goodFit.map((item) => (
                <p key={item}>{item}</p>
              ))}
            </article>
            <article className="fit-test-card fit-test-card--no">
              <h3>
                <CircleSlash aria-hidden="true" />
                Do not use it when
              </h3>
              {notFor.map((item) => (
                <p key={item}>{item}</p>
              ))}
            </article>
          </div>
          <div className="boundary-strip" role="note">
            <AlertTriangle aria-hidden="true" />
            <span>
              Evidence boundary: Provenance reviews authorized inputs and records what they prove.
              It should not be presented as runtime IDS, independent pentest proof, or a clean
              result when evidence is missing.
            </span>
          </div>
        </section>

        <section className="story-cta buyer-cta">
          <div>
            <h2>Start with one finding your team needs to resolve.</h2>
            <p>
              Prepare the scope and acceptance criteria before connecting source or artifacts.
              Existing customers can sign in to the protected console.
            </p>
          </div>
          <div className="buyer-cta__actions">
            <a
              className="story-button story-button--primary"
              href={openConsole}
              onClick={(event) => handleInternalNavigation(event, navigate)}
            >
              Sign in to Provenance
              <ArrowRight aria-hidden="true" />
            </a>
            <a
              className="story-button story-button--quiet story-button--book"
              href={publicDocsHref}
              target="_blank"
              rel="noreferrer"
            >
              Read product book
              <Download aria-hidden="true" />
            </a>
          </div>
        </section>
        <ProductVideos />
        <footer className="buyer-portfolio-footer">
          <span>Application evidence. Reviewer-owned decisions.</span>
          <a href="https://www.bwtr.ai/">Breakwater ↗</a>
          <a href="https://discover.bwtr.ai/">Discover ↗</a>
          <a href="https://response.bwtr.ai/">Response ↗</a>
          <a href="#evaluate">Plan an evaluation ↑</a>
        </footer>
      </main>
    </div>
  );
}
